// Core Libraries
import React from 'react'
import { connect } from 'react-redux'
import { get } from 'lodash'

// Actions
import actions from '../../actions/IssuerStatuses'
import issuerActions from '../../actions/Issuers'
import privateExchangeActions from '../../actions/PrivateExchanges'

// Components
import Component from '../common/Component'
import withRouter from '../../lib/withRouter'
import EditForm, { defaultPropTypes, connectEditForm } from '../common/EditForm'
import {
  IdeonInput, IdeonSelect, IdeonSwitch, IdeonAsyncField, IdeonFormActions
} from '../common/ideon/fields'
import SbcParserV2Panel from './SbcParserV2Panel'

const SBC_PARSER_V2 = 'sbc_parser_v2'

// Says what the switch does in BOTH positions. An earlier wording claimed
// behaviour was unchanged either way, which is only true of the off
// position -- on is precisely what routes eligible SBCs away from v1.
const SBC_PARSER_V2_DESCRIPTION = [
  'Off leaves this issuer status on the existing v1 parser.',
  'On makes its eligible SBCs available to Parser v2, once the global sbc_parser_v2 flag is on.'
].join(' ')

// PD-30386: the form's layout, as Tailwind utilities on the elements they
// describe. One row per line below.
//
// The form is a single column until `lg`, then four equal field columns plus a
// sidebar column for SbcParserV2Panel. A field opts into a narrower track by
// carrying a different col-span -- there is no per-field CSS and nothing in a
// stylesheet knows a field's name.
const FORM = [
  'pure-form pure-form-stacked',
  'grid grid-cols-1 gap-x-8 gap-y-6 items-start auto-rows-min',
  'lg:grid-cols-[repeat(4,minmax(0,1fr))_minmax(22rem,1.1fr)]'
].join(' ')

const FULL = 'lg:col-span-4'
const HALF = 'lg:col-span-2'
// Two scores per line from `lg`, four once there is room for them.
const QUARTER = 'lg:col-span-2 min-[1366px]:col-span-1'

// The sidebar spans every field ROW, and that count has to be a literal:
// `row-span-full` is `grid-row: 1 / -1`, which counts from the end of the
// EXPLICIT grid -- with no grid-template-rows there are no explicit tracks, so
// it collapses to a single row and the sidebar's height inflates row one.
// Rows are not fields: the dates share a row, and so do the scores (two rows
// until they fit four across, hence the extra row below `min-[1366px]`).
// IssuerStatusEdit.test.js fails if the field list drifts from these numbers.
const SIDEBAR = 'lg:col-start-5 lg:row-[1_/_span_11] min-[1366px]:row-[1_/_span_10] self-start'
const SEARCH_PLACEHOLDER = 'Type at least 2 characters to search'
// RemoteOptionsSelectField's own gate (app/frontend/src/components/common/RemoteOptionsSelectField.js)
// -- kept so a stray keystroke can't fire a request that could never match.
const MIN_SEARCH_LENGTH = 2

// PD-30386: the issuer_id label IssuerRemoteOptionsSelectField already used
// (['carrier_brand.name', 'licensed_state_code', 'issuer_id']), preserved for
// both the initial record (the loaded issuer status's nested `issuer`) and
// searched records (Issuers#index) -- both are IssuerSerializer output, so
// they share the same shape.
function issuerLabel(issuer) {
  const carrierBrandName = get(issuer, 'carrier_brand.name', issuer.carrier_brand_name)
  return [carrierBrandName, issuer.licensed_state_code, issuer.issuer_id].filter(Boolean).join(' - ')
}

function privateExchangeLabel(privateExchange) {
  return privateExchange.name
}

export class IssuerStatusEdit extends Component {
  static propTypes = defaultPropTypes

  get id() {
    return this.props.params.id
  }

  // PD-30386: sbc_parser_v2 is restricted to issuer administrators, and the
  // server enforces that by leaving it out of IssuerStatusPolicy's permitted
  // attributes. Reading that same list back is what disables the switch, so
  // the UI cannot drift from the policy the way a second role check would --
  // and an operator never gets an operable control whose value the save would
  // silently discard.
  get sbcParserV2Disabled() {
    return !get(this.props.entitlements, 'permitted_attributes', []).includes(SBC_PARSER_V2)
  }

  get sbcParserV2Title() {
    // The permitted set is IssuerStatusPolicy#admin?, which is the platform
    // administrator role OR issuer_administrator -- naming only one of them
    // sends the wrong person looking for the wrong approver.
    return this.sbcParserV2Disabled
      ? 'Only a platform administrator or issuer administrator can change SBC Parser v2'
      : SBC_PARSER_V2_DESCRIPTION
  }

  get requestInProgress() {
    return !!get(this.props.resourceActionStatuses, 'update.requestInProgress')
  }

  searchIssuers = (query) => {
    if ((query || '').trim().length < MIN_SEARCH_LENGTH) return Promise.resolve([])

    return this.props.indexIssuers({ search_term: query }).then((response) => (
      (response.records || []).map((record) => ({ value: String(record.id), label: issuerLabel(record) }))
    ))
  }

  searchPrivateExchanges = (query) => {
    if ((query || '').trim().length < MIN_SEARCH_LENGTH) return Promise.resolve([])

    return this.props.indexPrivateExchanges({ search_term: query }).then((response) => (
      (response.records || []).map((record) => ({ value: String(record.id), label: privateExchangeLabel(record) }))
    ))
  }

  // withRouter supplies `history` as react-router's navigate function.
  handleCancel = () => {
    this.props.history(this.props.redirectTo)
  }

  render() {
    return (
      <div className="ideon-ui-scope">
        {/* EditForm always renders its own legacy SubmitButton; this screen
          * supplies the wireframe's Save/Cancel pair instead, so that one is
          * hidden rather than removed -- EditForm stays untouched for the ~20
          * other screens that rely on it, and the real submit still runs
          * through the form's own onSubmit either way. */}
        <EditForm
          {...this.props}
          id={this.id}
          className={FORM}
          submitButtonClassName="hidden"
        >
          <IdeonAsyncField
            key="issuer_id"
            name="issuer_id"
            className={FULL}
            label="Issuer"
            associationKey="issuer"
            recordLabel={issuerLabel}
            onSearch={this.searchIssuers}
            placeholder={SEARCH_PLACEHOLDER}
          />

          <IdeonSelect
            key="state_id"
            name="state_id"
            className={FULL}
            label="State"
            options={this.props.configuration.states}
            cast={Number}
          />

          <IdeonSelect
            key="product_line"
            name="product_line"
            className={FULL}
            label="Product Line"
            options={this.props.configuration.product_lines}
          />

          <IdeonSelect
            key="plan_audience"
            name="plan_audience"
            className={FULL}
            label="Plan Audience"
            options={this.props.configuration.plan_audiences}
          />

          <IdeonInput
            key="active_from"
            name="active_from"
            className={HALF}
            label="Active From"
            type="date"
          />

          <IdeonInput
            key="active_until"
            name="active_until"
            className={HALF}
            label="Active Until"
            type="date"
          />

          <IdeonAsyncField
            key="private_exchange_id"
            name="private_exchange_id"
            className={FULL}
            label="Private Exchange"
            associationKey="private_exchange"
            recordLabel={privateExchangeLabel}
            onSearch={this.searchPrivateExchanges}
            placeholder={SEARCH_PLACEHOLDER}
          />

          <IdeonSwitch
            key="privileged"
            name="privileged"
            className={FULL}
            label="Privileged"
          />

          <IdeonInput
            key="pd_score"
            name="pd_score"
            className={QUARTER}
            label="PD Score"
            min={0}
            max={5}
          />

          <IdeonInput
            key="pr_score"
            name="pr_score"
            className={QUARTER}
            label="PR Score"
            min={0}
            max={5}
          />

          <IdeonInput
            key="sa_score"
            name="sa_score"
            className={QUARTER}
            label="SA Score"
            min={0}
            max={5}
          />

          <IdeonInput
            key="sbc_score"
            name="sbc_score"
            className={QUARTER}
            label="SBC Score"
            min={0}
            max={5}
          />

          <IdeonSwitch
            key={SBC_PARSER_V2}
            name={SBC_PARSER_V2}
            className={FULL}
            label="SBC Parser v2 is on for this issuer status"
            description={SBC_PARSER_V2_DESCRIPTION}
            disabled={this.sbcParserV2Disabled}
            title={this.sbcParserV2Title}
          />

          <IdeonFormActions
            key="form_actions"
            className={`${FULL} mt-2`}
            submitLabel="Save"
            onCancel={this.handleCancel}
            loading={this.requestInProgress}
          />

          <SbcParserV2Panel key="sbc_parser_v2_panel" className={SIDEBAR} />
        </EditForm>
      </div>
    )
  }
}

const mapDispatchToProps = {
  indexIssuers: issuerActions.index.main,
  indexPrivateExchanges: privateExchangeActions.index.main
}

const ConnectedIssuerStatusEdit = connect(null, mapDispatchToProps)(IssuerStatusEdit)

export default connectEditForm(withRouter(ConnectedIssuerStatusEdit), 'Issuer Status', 'issuer_statuses', actions)
